import { type ICameraInitializer, type IViewPortController } from '@/engine/app/interfaces';
import type { ViewsService } from '@/engine/app/Views.service';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { IThreeDViewPortDetails, ViewEntity } from '@/engine/app/models/viewEntity/view.entity.ts';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type * as THREE from 'three';
import type { IDisposable } from '@/engine/Utilities/IDisposable';

export class CameraInitializer implements ICameraInitializer {
  private readonly _viewportController: IViewPortController;

  private readonly _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _viewDetails: IThreeDViewPortDetails;

  private _orientationChangedSubscription?: IDisposable;

  private _imagesReadySubscription?: IDisposable;

  public constructor(
    viewportController: IViewPortController,
    viewsService: ViewsService,
    planningAlgorithm: PlanningAlgorithm,
    viewDetails: IThreeDViewPortDetails
  ) {
    this._viewportController = viewportController;
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._viewDetails = viewDetails;
  }

  public initialize(): void {
    const currentView: ViewEntity | null = this._viewsService.currentView;
    if (!currentView) {
      return;
    }

    const orientationDescriptor = currentView.getCameraOrientationDescriptor();
    if (orientationDescriptor) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
      this._setup3dCameraByOrientationDescriptor(orientationDescriptor, currentView.getCameraFittingFactor());
    } else {
      this._viewportController.setupCamera(this._viewDetails.orientation);
      this._viewportController.fitCameraToObjects(currentView.getCameraFittingFactor());
    }
  }

  private async _setup3dCameraByOrientationDescriptor(
    orientationDescriptor: string,
    cameraFittingFactor: number
  ): Promise<void> {
    const orientation = await this._planningAlgorithm.getValueAsync(orientationDescriptor, DT.Orientation);
    if (!orientation) {
      throw new Error('Orientation is not initialized');
    }
    this._viewportController.setupCamera({
      topVector: orientation.value.top,
      directionVector: orientation.value.dir.clone().negate()
    });

    this._viewportController.fitCameraToObjects(cameraFittingFactor);
  }

  public fitCameraToBox(
    minCorner: THREE.Vector3,
    maxCorner: THREE.Vector3,
    orientationTop: THREE.Vector3,
    orientationDir: THREE.Vector3
    /*fittingFactor: number*/
  ): void {
    this._viewportController.getCameraController().setCameraOrientation({
      topVector: orientationTop,
      directionVector: orientationDir.clone().negate()
    });
    this._viewportController.getCameraController().fitCameraToPoints([minCorner, maxCorner], { fittingFactor: 0.4 });
  }

  public dispose(): void {
    this._orientationChangedSubscription?.dispose();
    this._imagesReadySubscription?.dispose();
  }
}
